import { displayReceiptNumber } from './displayReceiptNumber.js';
import { formatPersonDisplayName } from './capitalizePersonName.js';
import { gstInclusiveBreakdown, formatReceiptInr } from './receiptGstBreakdown.js';
import { isOnlineReceiptMethod } from '../constants/receiptPaymentMethods.js';
import {
  formatAdminIstDate,
  formatAdminIstPaymentDate,
  formatAdminIstTime
} from './adminIstDateTime.js';
import { computePackageDates, formatPackageDateRange } from './packageDates.js';

const ORG = {
  name: 'Invincible',
  addressLines: [
    '308, 3rd Floor, University Plaza,',
    'Vijay Cross Roads, Navrangpura,',
    'Ahmedabad, Gujarat 380009'
  ],
  gstin: '24AABTI1271J1ZU',
  hsn: '996313'
};

/** Admin receipt logo (deployed at admin/public/images/logo.png). */
export function resolveReceiptLogoUrl(origin = '') {
  const base = String(origin || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  return `${base}/images/logo.png`;
}

export function parseGatewayChargeFromNotes(notes) {
  const m = String(notes || '').match(/Gateway:\s*([0-9]+(?:\.[0-9]+)?)/i);
  const n = m ? Number(m[1]) : 0;
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
}

export function parsePaidGrossFromNotes(notes) {
  const m = String(notes || '').match(/Paid:\s*([0-9]+(?:\.[0-9]+)?)/i);
  const n = m ? Number(m[1]) : 0;
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
}

const formatShortDate = formatAdminIstDate;
const formatShortTime = formatAdminIstTime;

function formatLongDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime())
    ? '—'
    : x.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'Asia/Kolkata'
      });
}

function formatEventDateRange(batch) {
  return formatPackageDateRange(batch?.startDate, batch?.endDate, formatShortDate);
}

function resolveReceiptPackageDates(booking, snapshot, pkg, batch, trek) {
  if (snapshot?.packageStartDate || snapshot?.packageEndDate) {
    return formatPackageDateRange(snapshot.packageStartDate, snapshot.packageEndDate, formatShortDate);
  }
  const event = batch || (snapshot?.batchStartDate
    ? { startDate: snapshot.batchStartDate, endDate: snapshot.batchEndDate }
    : null);
  const packages = trek?.packages || [];
  if (event && pkg) {
    const { packageStartDate, packageEndDate } = computePackageDates(event, pkg, packages);
    if (packageStartDate || packageEndDate) {
      return formatPackageDateRange(packageStartDate, packageEndDate, formatShortDate);
    }
  }
  if (event) return formatEventDateRange(event);
  return '—';
}

function participantAge(traveler) {
  if (traveler?.age != null && traveler.age !== '') return String(traveler.age);
  const raw = traveler?.dateOfBirth || traveler?.birthdate || traveler?.dob || null;
  if (!raw) return '—';
  const d = new Date(String(raw).slice(0, 10));
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age <= 130 ? String(age) : '—';
}

function resolvePackage(booking, trek) {
  if (booking?.joinFromPackage?.id) return booking.joinFromPackage;
  if (booking?.registration?.package?.id) return booking.registration.package;

  const packages = trek?.packages || [];
  const joinId = booking?.joinFromPackageId ?? booking?.registration?.packageId ?? null;
  if (joinId) {
    const fromTrek = packages.find((p) => String(p.id) === String(joinId));
    if (fromTrek) return fromTrek;
  }
  return packages.find((p) => p.isMain) || packages[0] || null;
}

function buildParticipantRows(booking, { limit } = {}) {
  const travelers = Array.isArray(booking?.travelers)
    ? booking.travelers.filter((t) => t && typeof t === 'object')
    : [];
  let rows;
  if (!travelers.length) {
    const reg = booking?.registration;
    const name = formatPersonDisplayName(reg?.firstName, reg?.lastName) || 'Guest Traveller';
    rows = [{ index: 1, name, age: '—' }];
  } else {
    rows = travelers.map((t, i) => ({
      index: i + 1,
      name: formatPersonDisplayName(t.firstName, t.lastName) || 'Guest Traveller',
      age: participantAge(t)
    }));
  }
  if (limit != null && limit > 0) {
    return rows.slice(0, limit).map((r, i) => ({ ...r, index: i + 1 }));
  }
  return rows;
}

function buildParticipantRowsFromSnapshot(snapshot, { limit } = {}) {
  const list = Array.isArray(snapshot?.participants) ? snapshot.participants : [];
  if (!list.length) {
    return [{ index: 1, name: 'Guest Traveller', age: '—' }];
  }
  const slice = limit != null && limit > 0 ? list.slice(0, limit) : list;
  return slice.map((t, i) => ({
    index: i + 1,
    name: formatPersonDisplayName(t.firstName, t.lastName) || 'Guest Traveller',
    age: participantAge(t)
  }));
}

function formatInvoicedToName(participants) {
  const names = (participants || [])
    .map((p) => String(p?.name || '').trim())
    .filter(Boolean);
  if (!names.length) return 'Guest Traveller';
  if (names.length === 1) return names[0];
  return names.join(', ');
}

/** Parse "Participants: A, B, C" from Razorpay receipt notes. */
function participantsFromReceiptNotes(notes) {
  const text = String(notes || '');
  const group = text.match(/Participants:\s*([^|]+)/i);
  if (group) {
    return group[1]
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean)
      .map((name, i) => ({ index: i + 1, name, age: '—' }));
  }
  const single = text.match(/Participant:\s*([^|]+)/i);
  if (single) {
    const name = single[1].trim();
    if (name) return [{ index: 1, name, age: '—' }];
  }
  return [];
}

function resolveReceiptParticipants(receipt, booking, snapshot) {
  const isOnline = isOnlineReceiptMethod(receipt?.method);
  const apiGroup = Array.isArray(receipt?.groupParticipants) ? receipt.groupParticipants : [];
  const bookingGroup = Array.isArray(booking?.groupParticipants) ? booking.groupParticipants : [];
  const groupMembers = Array.isArray(booking?.groupMembers) ? booking.groupMembers : [];
  const snapList = Array.isArray(snapshot?.participants) ? snapshot.participants : [];
  const fromNotes = participantsFromReceiptNotes(receipt?.notes);

  if (apiGroup.length > 1) {
    return buildParticipantRowsFromSnapshot({ participants: apiGroup });
  }
  if (bookingGroup.length > 1) {
    return buildParticipantRowsFromSnapshot({ participants: bookingGroup });
  }
  if (groupMembers.length > 1) {
    return groupMembers.map((m, i) => ({
      index: i + 1,
      name: String(m?.name || '').trim() || 'Guest Traveller',
      age: m?.age != null && m.age !== '' ? String(m.age) : '—'
    }));
  }
  if (snapList.length > 1) return buildParticipantRowsFromSnapshot(snapshot);
  if (fromNotes.length > 1) return fromNotes;

  if (apiGroup.length === 1) {
    return buildParticipantRowsFromSnapshot({ participants: apiGroup });
  }
  if (bookingGroup.length === 1) {
    return buildParticipantRowsFromSnapshot({ participants: bookingGroup });
  }
  if (groupMembers.length === 1) {
    return groupMembers.map((m, i) => ({
      index: 1,
      name: String(m?.name || '').trim() || 'Guest Traveller',
      age: m?.age != null && m.age !== '' ? String(m.age) : '—'
    }));
  }
  if (snapList.length) {
    if (isOnline) return buildParticipantRowsFromSnapshot(snapshot);
    return buildParticipantRowsFromSnapshot(snapshot, { limit: 1 });
  }
  if (fromNotes.length === 1) return fromNotes;
  
  if (isOnline) return buildParticipantRows(booking);
  return buildParticipantRows(booking, { limit: 1 });
}

function resolveReceiptSnapshot(receipt) {
  const raw = receipt?.snapshotJson ?? receipt?.snapshot_json ?? null;
  if (!raw || typeof raw !== 'object') return null;
  if (!raw.trekName && !raw.packageName && !raw.bookingCode && !raw.participants?.length) return null;
  return raw;
}

function receiptStyles() {
  return `
    body { font-family: -apple-system, BlinkMacSystemFont, system-ui, sans-serif; margin: 0; padding: 32px 16px; background: #f3f4f6; }
    .invoice-shell { max-width: 720px; margin: 0 auto; background: #ffffff; padding: 32px 40px 40px; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12); }
    .invoice-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; }
    .logo-wrap img { height: 56px; max-width: 200px; object-fit: contain; }
    .title { font-size: 24px; color: #0f172a; font-weight: 600; text-align: right; }
    .muted { color: #6b7280; font-size: 12px; }
    .meta-row { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 16px; flex-wrap: wrap; gap: 8px; }
    .meta-date-wrap { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; min-width: 220px; }
    .meta-label { color: #6b7280; }
    .meta-value { color: #111827; font-weight: 500; }
    .section { margin-top: 20px; }
    .section-title { font-size: 13px; font-weight: 600; margin-bottom: 4px; }
    .section-block { border: 1px solid #e5e7eb; border-radius: 4px; padding: 12px 14px; font-size: 13px; }
    .event-detail-row { margin: 4px 0; }
    .event-detail-row strong { color: #374151; min-width: 88px; display: inline-block; }
    .two-col { display: flex; justify-content: space-between; gap: 24px; }
    .two-col > div { flex: 1; }
    .org-name { font-weight: 600; margin-bottom: 4px; }
    .address-line { margin: 0; color: #4b5563; font-size: 12px; }
    .org-meta { margin-top: 8px; color: #4b5563; font-size: 12px; }
    .org-meta strong { font-weight: 600; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .summary-table { margin-top: 12px; }
    .summary-table th { background: #f9fafb; font-weight: 600; text-align: left; padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .summary-table td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .summary-table td.amount { text-align: right; white-space: nowrap; }
    .summary-table tfoot td { font-weight: 700; }
    .participants-table { margin-top: 8px; }
    .participants-table th { background: #f9fafb; font-weight: 600; text-align: left; padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .participants-table td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
    .note { margin-top: 18px; font-size: 11px; color: #4b5563; }
    .note strong { font-weight: 600; }
    @media print {
      body { background: #ffffff; padding: 0; }
      .invoice-shell { box-shadow: none; margin: 0; max-width: 100%; }
    }
  `;
}

/**
 * Print-friendly tax receipt for admin Receipts page (one payment receipt row).
 */
export function buildAdminTaxReceiptHtml(receipt, options = {}) {
  const snapshot = resolveReceiptSnapshot(receipt);
  const booking = receipt?.booking;
  const batch = booking?.batch;
  const trek = batch?.trek;
  const pkg = resolvePackage(booking, trek);
  const participants = resolveReceiptParticipants(receipt, booking, snapshot);
  const invoicedToName = formatInvoicedToName(participants);
  const bookingCode = snapshot?.bookingCode || booking?.bookingCode || '';
  const registrationIdDisplay = (() => {
    const id =
      snapshot?.registrationId ??
      booking?.registrationId ??
      booking?.registration?.id ??
      null;
    return id != null && id !== '' ? String(id) : '—';
  })();
  const receiptNo = displayReceiptNumber(receipt) || bookingCode || '—';
  const tripName = snapshot?.trekName || trek?.name || '—';
  const packageDates = resolveReceiptPackageDates(booking, snapshot, pkg, batch, trek);
  const packageName = pkg?.name || snapshot?.packageName || '—';
  const packageDesc = String(pkg?.description || snapshot?.packageDescription || '').trim();
  const receiptCreatedAt = receipt?.paidAt || receipt?.createdAt || null;
  const invoiceDate = formatShortDate(receiptCreatedAt);
  const invoiceTime = formatShortTime(receiptCreatedAt);

  const isOnline = isOnlineReceiptMethod(receipt?.method);
  const gatewayCharge = isOnline ? parseGatewayChargeFromNotes(receipt?.notes) : 0;
  const paidGross = isOnline
    ? parsePaidGrossFromNotes(receipt?.notes) || Number(receipt?.amount ?? 0) + gatewayCharge
    : Number(receipt?.amount ?? 0);
  const baseAmount = Math.max(0, paidGross - gatewayCharge) || Number(receipt?.amount ?? 0);
  const { subTotal, sgst, cgst, gstTotal, grandTotal } = gstInclusiveBreakdown(baseAmount);

  const methodLabel = isOnline
    ? 'Online (Razorpay)'
    : receipt?.method === 'cash'
      ? 'Cash'
      : 'Office';
  const paymentDate = formatAdminIstPaymentDate(receipt?.paidAt || receipt?.createdAt);

  const logoUrl = resolveReceiptLogoUrl(options.origin);
  const participantRowsHtml = participants
    .map(
      (p) => `
          <tr>
            <td>${p.index}</td>
            <td>${p.name}</td>
            <td>${p.age}</td>
          </tr>`
    )
    .join('');

  const gatewayRow =
    gatewayCharge > 0
      ? `
          <tr>
            <td>Bank / Payment Gateway Charges (2%)</td>
            <td class="amount">₹ ${formatReceiptInr(gatewayCharge)}/-</td>
          </tr>`
      : '';

  const totalPaidRow =
    gatewayCharge > 0
      ? `
          <tr>
            <td><strong>Total Amount Paid</strong> <span class="muted">(incl. bank charges)</span></td>
            <td class="amount"><strong>₹ ${formatReceiptInr(paidGross)}/-</strong></td>
          </tr>`
      : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Tax Invoice ${bookingCode}</title>
  <style>${receiptStyles()}</style>
</head>
<body>
  <div class="invoice-shell">
    <div class="invoice-header">
      <div class="logo-wrap">
        <img src="${logoUrl}" alt="Invincible logo" />
      </div>
      <div class="title">Tax Invoice</div>
    </div>

    <div class="meta-row">
      <div class="meta-date-wrap">
        <div>
          <span class="meta-label">Date:</span>
          <span class="meta-value">${invoiceDate}</span>
        </div>
        <span class="meta-value">${invoiceTime}</span>
      </div>
      <div>
        <span class="meta-label">Receipt No:</span>
        <span class="meta-value">${receiptNo}</span>
      </div>
      <div>
        <span class="meta-label">Registration ID:</span>
        <span class="meta-value">${registrationIdDisplay}</span>
      </div>
    </div>

    <div class="two-col section">
      <div>
        <div class="section-title">Invoiced To:</div>
        <div class="section-block">
          <div class="org-name">${invoicedToName}</div>
        </div>
      </div>
      <div>
        <div class="section-title">Pay To:</div>
        <div class="section-block">
          <div class="org-name">${ORG.name}</div>
          ${ORG.addressLines.map((l) => `<p class="address-line">${l}</p>`).join('')}
          <div class="org-meta">
            <div><strong>GSTIN:</strong> ${ORG.gstin}</div>
            <div><strong>HSN Code:</strong> ${ORG.hsn}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Event Details</div>
      <div class="section-block">
        <div class="event-detail-row"><strong>Event:</strong> ${tripName}</div>
        <div class="event-detail-row"><strong>Package:</strong> ${packageName}${packageDesc ? ` — ${packageDesc}` : ''}</div>
        <div class="event-detail-row"><strong>Date:</strong> ${packageDates}</div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Payment</div>
      <div class="section-block muted">
        Method: <strong>${methodLabel}</strong> · Paid on: <strong>${paymentDate}</strong>
        ${isOnline && gatewayCharge > 0 ? ` · Includes bank charges: <strong>₹ ${formatReceiptInr(gatewayCharge)}</strong>` : ''}
      </div>
    </div>

    <div class="section">
      <div class="section-title">Amount Summary</div>
      <table class="summary-table">
        <thead>
          <tr>
            <th>Details</th>
            <th class="amount">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${tripName} — ${packageName} <span class="muted">(price incl. 5% GST)</span></td>
            <td class="amount">₹ ${formatReceiptInr(grandTotal)}/-</td>
          </tr>
          <tr>
            <td>Taxable amount <span class="muted">(excl. 5% GST)</span></td>
            <td class="amount">₹ ${formatReceiptInr(subTotal)}/-</td>
          </tr>
          <tr>
            <td>SGST @ 2.5%</td>
            <td class="amount">₹ ${formatReceiptInr(sgst)}/-</td>
          </tr>
          <tr>
            <td>CGST @ 2.5%</td>
            <td class="amount">₹ ${formatReceiptInr(cgst)}/-</td>
          </tr>
          <tr>
            <td>GST total <span class="muted">(5%)</span></td>
            <td class="amount">₹ ${formatReceiptInr(gstTotal)}/-</td>
          </tr>
          ${gatewayRow}
        </tbody>
        <tfoot>
          ${totalPaidRow || `
          <tr>
            <td>Total <span class="muted">(GST inclusive)</span></td>
            <td class="amount">₹ ${formatReceiptInr(grandTotal)}/-</td>
          </tr>`}
        </tfoot>
      </table>
    </div>

    <div class="section">
      <div class="section-title">Participants</div>
      <table class="participants-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Age</th>
          </tr>
        </thead>
        <tbody>${participantRowsHtml}
        </tbody>
      </table>
    </div>

    <p class="note">
      <strong>NOTE:</strong> This is a computer generated receipt and does not require physical signature.
    </p>
    <p class="note">
      <strong>NOTE:</strong> This PDF can be used for reporting purpose. Printout is not required.
    </p>
  </div>

  <script>
    window.onload = function () { window.print(); };
  </script>
</body>
</html>`;
}
